/**
 * Booking-related types and interfaces
 */

import { ImageInfo, LocationInfo, TaxDetail, BookingType, PaginationParams } from './common';

// Booking creation request
export interface CreateBookingRequest {
  title: string;
  spaceId: number;
  startDate: Date | string;
  endDate: Date | string;
  bookingType: BookingType;
  discountRate?: number;
  isWithoutCancel?: boolean;
  numberOfAttendees: number;
  returnURL?: string;
  amenities?: BookingAmenity[];
  attendees?: BookingAttendee[];
  isCreditBooking?: boolean;
  companyGuid?: string;
  isAccountMigrated?: boolean;
}

// Booking amenity for requests
export interface BookingAmenity {
  id: number;
  totalQuantity: number;
  amenityDiscountRate?: number;
}

// Property amenity from external API
export interface PropertyAmenity {
  id: number;
  name: string;
  priceType: string; // "0" = free, "1" = paid
  amenityIconName?: string;
  weworkIdentifier?: string;
  amenityUnitPrice?: number;
  amenitySetupPrice?: number;
}

// Booking attendee
export interface BookingAttendee {
  customerId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

// Basic booking response
export interface BookingResponse {
  id: number;
  title: string;
  startDate: string;
  endDate: string;
  spaceName: string;
  totalAmount: number;
  status: string;
  message?: string;
}

// Full booking data with all details
export interface FullBookingData {
  id: number;
  title: string;
  companyId: number;
  customerId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  startDate: string;
  endDate: string;
  propertyGUID: string;
  propertyId: number;
  propertyName: string;
  propertyAddress?: string;
  location?: LocationInfo;
  createdOn: string;
  lastModifiedOn: string;
  operatorId: number;
  operatorName: string;
  propertyTimezoneOffset?: string;
  spaceId: number;
  spaceName: string;
  spaceTypeId: number;
  spaceTypeName: string;
  bookingMethod: string;
  bookingStatus: string;
  bookingType: string;
  bookingUnit: string;
  discountRate: number;
  numberOfAttendees: number;
  duration: number;
  totalUnits: number;
  spaceUnitPrice: number;
  spaceDiscountAmount: number;
  spaceUnitSetupPrice: number;
  spaceTaxTotal: number;
  spaceTotal: number;
  paymentMethod: number;
  amenities: any[];
  attendees: any[];
  taxDetail: any[];
  isBookingApprovalOn: boolean;
  companyGuid?: string;
  spaceImageInfo: ImageInfo[];
  isCreditBooking: boolean;
  isFranchiseBooking: boolean;
  franchiseDestinationId: number;
  bookingExternalReference: number;
  propertyExternalReference?: string;
  spaceExternalReference?: string;
  paymentAmount: number;
  paymentUrl?: string;
  success: number;
  message?: string;
  errorMessage?: string;
  errorDetails?: string;
  originalBookingId: number;

  // Business Logic Fields - Populated by Backend Business Service
  // These fields provide frontend with business-logic-determined data
  
  /**
   * Available actions for this booking determined by backend business rules.
   * Frontend should render exactly these actions - no business logic on UI side.
   * Examples: ["view", "cancel", "edit"]
   */
  availableActions?: string[];

  /**
   * Calculated booking status based on backend business rules.
   * This is separate from bookingStatus (which comes from external API)
   * Examples: "upcoming", "active", "past", "cancelled"
   */
  calculatedStatus?: string;

  /**
   * Total booking amount including space price and all taxes.
   * Calculated by backend business service: spaceUnitPrice + spaceTaxTotal + (sum of all tax details)
   * This provides a single, authoritative total for frontend display.
   */
  totalBookingAmount?: number;
}

// Booking list response
export interface BookingListResponse {
  bookings: FullBookingData[];
  totalRows: number;
}

// Booking search filters
export interface BookingSearchFilters extends PaginationParams {
  fromDate?: Date;
  toDate?: Date;
  propertyId?: number;
}

// Amenity pricing breakdown
export interface AmenityPricingDetail {
  id: number;
  name: string;
  quantity: number;
  unitPrice: number;
  setupPrice: number;
  subtotal: number;
  taxAmount: number;
  total: number;
}

// Price calculation result
export interface PriceCalculationResult {
  basePrice: number;
  duration: number;
  durationUnit: string;
  baseAmount: number;
  totalTaxRate: number;
  taxAmount: number;
  finalAmount: number;
  taxBreakdown: TaxDetail[];
  amenityBreakdown?: AmenityPricingDetail[];
  amenityTotal?: number;
}

// Amenity for pricing calculations (matches UI SelectedAmenity)
export interface PricingAmenity {
  id: number;
  quantity: number;
}

// Price calculation request
export interface PriceCalculationRequest {
  workspaceId: string;
  fromDate: Date;
  toDate: Date;
  bookingType?: BookingType;
  amenities?: PricingAmenity[];
}

// Cancel booking response
export interface CancelBookingResponse {
  success: number;
  message: string;
  errorMessage?: string;
}

/**
 * Response containing user's top most booked spaces with analytics
 */
export interface TopUserSpacesResponse {
  topSpaces: TopUserSpace[];
  hasBookingHistory: boolean;
  totalBookingsAnalyzed: number;
  totalUniqueSpaces: number;
  calculationMethod: string;
  timeframeDays: number;
}

/**
 * Individual space in the user's top list with booking analytics
 */
export interface TopUserSpace {
  // Space Details
  spaceId: number;
  workspaceGuid: string;
  name: string;
  location: string;
  propertyName: string;
  price: number;
  bookingType: string;
  images: string[];
  amenities: string[];
  capacity: number;
  
  // Booking Analytics
  bookingCount: number;
  totalHoursBooked: number;
  averageBookingDuration: number;
  firstBookedDate: string;
  lastBookedDate: string;
  analyticsScore: number;
  bookingPattern: string; // "Weekly", "Monthly", "Occasional"
  
  // UI Helper Properties
  formattedFrequency: string; // "Booked 5 times"
  formattedLastVisit: string; // "Last visited 2 weeks ago"
  isRecentlyUsed: boolean; // Used in last 30 days
}

/**
 * Calculation methods for determining "most booked" spaces
 */
export enum BookingCalculationMethod {
  BookingCount = 'BookingCount',
  TotalDuration = 'TotalDuration',
  RecencyWeighted = 'RecencyWeighted',
  CompositeScore = 'CompositeScore'
} 